import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { App } from "antd";
import AdminPagination from "@/components/admin/AdminPagination";
import AdminLayout from "@/components/layout/AdminLayout";
import AdminPageTitle from "@/components/admin/AdminPageTitle";
import {
  createStudy,
  getStudyErrorMessage,
  searchStudies,
  updateStudy,
  type Study,
  type StudySaveInput,
} from "@/services/studies";
import StudiesTable from "./components/StudiesTable";
import NewStudyModal from "./components/NewStudyModal";

const PAGE_SIZE = 20;

export default function StudiesPage() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [studies, setStudies] = useState<Study[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [showNewModal, setShowNewModal] = useState(false);
  const [editStudy, setEditStudy] = useState<Study | null>(null);

  const fetchList = useCallback(
    async (nextPage: number, keyword: string) => {
      setLoading(true);
      try {
        const result = await searchStudies(keyword, nextPage, PAGE_SIZE);
        setStudies(result.data);
        setTotal(result.count);
        setPage(result.page);
      } catch (error) {
        message.error(getStudyErrorMessage(error, t("studies.loadFailed")));
      } finally {
        setLoading(false);
      }
    },
    [message, t],
  );

  useEffect(() => {
    void fetchList(1, "");
  }, [fetchList]);

  const handleSearch = () => {
    const nextSearch = searchInput.trim();
    setSearch(nextSearch);
    void fetchList(1, nextSearch);
  };

  const handleReset = () => {
    setSearchInput("");
    setSearch("");
    void fetchList(1, "");
  };

  const handleSaveNew = async (input: StudySaveInput) => {
    await createStudy(input);
    await fetchList(1, search);
    setShowNewModal(false);
    message.success(t("studies.toast.created"));
  };

  const handleSaveEdit = async (input: StudySaveInput) => {
    if (!editStudy) return;
    await updateStudy(editStudy.id, input);
    await fetchList(page, search);
    setEditStudy(null);
    message.success(t("studies.toast.updated"));
  };

  const handleView = (study: Study, memberSource?: "auto" | "manual") => {
    const query = memberSource ? `?memberSource=${memberSource}` : "";
    navigate(`/admin/studies/${study.id}${query}`);
  };

  const hasActiveFilters = search !== "";

  return (
    <AdminLayout>
      <div>
        <div className="flex items-start justify-between mb-6">
          <div>
            <AdminPageTitle title={t("studies.title")} tooltip={t("studies.subtitle")} />
          </div>
          <button
            onClick={() => setShowNewModal(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white rounded-lg transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
            style={{ backgroundColor: "#830051" }}
          >
            <i className="ri-add-line text-sm"></i>
            {t("studies.newStudy")}
          </button>
        </div>

        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              handleSearch();
            }}
          >
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="search"
                placeholder={t("studies.searchPlaceholder")}
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                className="w-40 lg:w-52 xl:w-72 pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-gray-300 bg-white"
              />
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-line text-sm"></i>
                {t("studies.reset")}
              </button>
            )}
            <button
              type="submit"
              className="px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: "#830051" }}
            >
              {t("studies.searchBtn")}
            </button>
          </form>

          <div className="flex-1" />
          <span className="text-xs text-gray-400">
            {total} {total !== 1 ? t("common.studies") : t("common.study")}
          </span>
        </div>

        <StudiesTable
          studies={studies}
          loading={loading}
          onView={handleView}
          onEdit={setEditStudy}
        />

        <AdminPagination
          total={total}
          page={page}
          pageSize={PAGE_SIZE}
          loading={loading}
          onPageChange={(nextPage) => void fetchList(nextPage, search)}
        />

        {(showNewModal || editStudy) && (
          <NewStudyModal
            initialStudy={editStudy}
            onClose={() => {
              setShowNewModal(false);
              setEditStudy(null);
            }}
            onSave={editStudy ? handleSaveEdit : handleSaveNew}
          />
        )}
      </div>
    </AdminLayout>
  );
}
